import type {
  BoardRender,
  CoordinatePlanePayload,
  GeometryPayload,
  NumberLinePayload,
} from './types';

function extendRange(min: number, max: number, nextMin: number, nextMax: number) {
  const useMin = Number.isFinite(min) ? min : nextMin;
  const useMax = Number.isFinite(max) ? max : nextMax;
  return {
    min: Math.min(useMin, nextMin),
    max: Math.max(useMax, nextMax),
  };
}

function addUnique<T>(base: T[], delta: T[], keyFn: (item: T) => string): T[] {
  const seen = new Set(base.map(keyFn));
  const merged = [...base];
  for (const item of delta) {
    const key = keyFn(item);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(item);
  }
  return merged;
}

function mergeNumberLine(
  base: NumberLinePayload | undefined,
  delta: NumberLinePayload,
): NumberLinePayload {
  if (!base) return delta;
  const range = extendRange(base.min, base.max, delta.min, delta.max);
  return {
    min: range.min,
    max: range.max,
    tickStep: base.tickStep ?? delta.tickStep,
    points: addUnique(base.points ?? [], delta.points ?? [], (p) => `${p.x}|${p.style ?? ''}|${p.label ?? ''}`),
    intervals: addUnique(base.intervals ?? [], delta.intervals ?? [], (i) => `${i.start}|${i.end}|${i.startInclusive ? 1 : 0}|${i.endInclusive ? 1 : 0}|${i.label ?? ''}`),
  };
}

function mergeCoordinatePlane(
  base: CoordinatePlanePayload | undefined,
  delta: CoordinatePlanePayload,
): CoordinatePlanePayload {
  if (!base) return delta;
  const rangeX = extendRange(base.xMin, base.xMax, delta.xMin, delta.xMax);
  const rangeY = extendRange(base.yMin, base.yMax, delta.yMin, delta.yMax);
  return {
    xMin: rangeX.min,
    xMax: rangeX.max,
    yMin: rangeY.min,
    yMax: rangeY.max,
    xStep: base.xStep ?? delta.xStep,
    yStep: base.yStep ?? delta.yStep,
    points: addUnique(base.points ?? [], delta.points ?? [], (p) => `${p.x}|${p.y}|${p.label ?? ''}|${p.style ?? ''}|${p.color ?? ''}`),
    segments: addUnique(base.segments ?? [], delta.segments ?? [], (s) => `${s.x1}|${s.y1}|${s.x2}|${s.y2}|${s.label ?? ''}|${s.color ?? ''}|${s.dashed ? 1 : 0}`),
    lines: addUnique(base.lines ?? [], delta.lines ?? [], (l) => `${l.form}|${l.m ?? ''}|${l.b ?? ''}|${l.a ?? ''}|${l.bStd ?? ''}|${l.c ?? ''}|${l.x1 ?? ''}|${l.y1 ?? ''}|${l.x2 ?? ''}|${l.y2 ?? ''}|${l.label ?? ''}|${l.color ?? ''}|${l.dashed ? 1 : 0}`),
  };
}

function mergeGeometry(
  base: GeometryPayload | undefined,
  delta: GeometryPayload,
): GeometryPayload {
  if (!base) return delta;
  const rangeX = extendRange(base.xMin, base.xMax, delta.xMin, delta.xMax);
  const rangeY = extendRange(base.yMin, base.yMax, delta.yMin, delta.yMax);
  return {
    xMin: rangeX.min,
    xMax: rangeX.max,
    yMin: rangeY.min,
    yMax: rangeY.max,
    xStep: base.xStep ?? delta.xStep,
    yStep: base.yStep ?? delta.yStep,
    showAxis: base.showAxis ?? delta.showAxis,
    showGrid: base.showGrid ?? delta.showGrid,
    points: addUnique(base.points ?? [], delta.points ?? [], (p) => `${p.x}|${p.y}|${p.label ?? ''}|${p.style ?? ''}|${p.color ?? ''}`),
    segments: addUnique(base.segments ?? [], delta.segments ?? [], (s) => `${s.from[0]}|${s.from[1]}|${s.to[0]}|${s.to[1]}|${s.label ?? ''}|${s.color ?? ''}|${s.dashed ? 1 : 0}`),
    rays: addUnique(base.rays ?? [], delta.rays ?? [], (r) => `${r.from[0]}|${r.from[1]}|${r.through[0]}|${r.through[1]}|${r.label ?? ''}|${r.color ?? ''}|${r.dashed ? 1 : 0}`),
    lines: addUnique(base.lines ?? [], delta.lines ?? [], (l) => `${l.p1[0]}|${l.p1[1]}|${l.p2[0]}|${l.p2[1]}|${l.label ?? ''}|${l.color ?? ''}|${l.dashed ? 1 : 0}`),
    circles: addUnique(base.circles ?? [], delta.circles ?? [], (c) => `${c.center[0]}|${c.center[1]}|${c.radius ?? ''}|${c.through ? `${c.through[0]}|${c.through[1]}` : ''}|${c.label ?? ''}|${c.color ?? ''}`),
    angles: addUnique(base.angles ?? [], delta.angles ?? [], (a) => `${a.vertex[0]}|${a.vertex[1]}|${a.p1[0]}|${a.p1[1]}|${a.p2[0]}|${a.p2[1]}|${a.label ?? ''}|${a.color ?? ''}`),
    polygons: addUnique(base.polygons ?? [], delta.polygons ?? [], (p) => `${p.vertices.map((v) => `${v.x},${v.y}`).join(';')}|${p.label ?? ''}|${p.color ?? ''}|${p.fillColor ?? ''}|${p.opacity ?? ''}`),
  };
}

export function mergeDiagramPayload(
  render: BoardRender,
  base: NumberLinePayload | CoordinatePlanePayload | GeometryPayload | undefined,
  delta: NumberLinePayload | CoordinatePlanePayload | GeometryPayload,
) {
  if (render === 'number_line') {
    return mergeNumberLine(base as NumberLinePayload | undefined, delta as NumberLinePayload);
  }
  if (render === 'coordinate_plane') {
    return mergeCoordinatePlane(base as CoordinatePlanePayload | undefined, delta as CoordinatePlanePayload);
  }
  if (render === 'geometry') {
    return mergeGeometry(base as GeometryPayload | undefined, delta as GeometryPayload);
  }
  return delta;
}
